// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

export default function Seo({
  yoast,
  title,
  description,
  lang,
  meta,
  author,
  schemaMarkup,
  socialImageUrl,
  locationData,
}) {
  const SITE_URL = process.env.GATSBY_SITE_URL;
  const SITE_CODE = process.env.GATSBY_SITE_CODE;

  const { wp, defaultThumbnail } = useStaticQuery(graphql`
    query {
      wp {
        generalSettings {
          title
          description
        }
      }

      defaultThumbnail: file(relativePath: { eq: "meta/thumbnail.jpg" }) {
        publicURL
      }
    }
  `);

  const titleOverride = title
    ? `${title} | ${wp?.generalSettings?.title}`
    : false;

  let canonical = null;
  const pathname = locationData?.pathname;

  if (pathname) canonical = process.env.GATSBY_SITE_URL + locationData.pathname;

  let opengraphImage = yoast?.opengraphImage?.publicUrl
    ? `${SITE_URL}${yoast?.opengraphImage?.publicUrl}`
    : `${SITE_URL}${defaultThumbnail?.publicURL}`;
  let twitterImage = yoast?.twitterImage?.publicUrl
    ? `${SITE_URL}${yoast?.twitterImage?.publicUrl}`
    : `${SITE_URL}${defaultThumbnail?.publicURL}`;

  if (socialImageUrl) {
    opengraphImage = `${SITE_URL}${socialImageUrl}`;
    twitterImage = opengraphImage;
  }

  const finalMeta = {
    title: titleOverride || yoast?.title || wp?.generalSettings?.title,
    description:
      description || yoast?.metaDesc || wp?.generalSettings?.description,
    author,
    metaRobotsNoindex: yoast?.metaRobotsNoindex,
    metaRobotsNofollow: yoast?.metaRobotsNofollow,
    opengraph: {
      title:
        titleOverride ||
        yoast?.opengraphTitle ||
        yoast?.title ||
        wp?.generalSettings?.title,
      description:
        description ||
        yoast?.opengraphDescription ||
        yoast?.metaDesc ||
        wp?.generalSettings?.description,
      type: yoast?.opengraphType,
      siteName: yoast?.opengraphSiteName,
      author,
      publishedTime: yoast?.opengraphPublishedTime,
      modifiedTime: yoast?.opengraphModifiedTime,
      image: {
        url: opengraphImage,
        width: yoast?.opengraphImage?.width || 1200,
        height: yoast?.opengraphImage?.height || 630,
      },
    },
    twitter: {
      title:
        titleOverride ||
        yoast?.twitterTitle ||
        yoast?.title ||
        wp?.generalSettings?.title,
      description:
        description ||
        yoast?.twitterDescription ||
        yoast?.metaDesc ||
        wp?.generalSettings?.description,
      image: {
        url: twitterImage,
      },
    },
  };

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={finalMeta.title}
      link={[{ rel: 'canonical', href: canonical }]}
      meta={[
        {
          name: `robots`,
          content: `${finalMeta?.metaRobotsNoindex || 'index'}, ${
            finalMeta?.metaRobotsNofollow || 'follow'
          }`,
        },
        {
          name: `description`,
          content: finalMeta.description,
        },
        {
          name: 'author',
          author: finalMeta.author,
        },
        {
          property: `og:title`,
          content: finalMeta.opengraph.title,
        },
        {
          property: `og:description`,
          content: finalMeta.opengraph.description,
        },
        {
          property: `og:article:author`,
          content: finalMeta.opengraph.author,
        },
        {
          property: `og:type`,
          content: finalMeta.opengraph.type,
        },
        {
          property: `og:site_name`,
          content: finalMeta.opengraph.siteName,
        },
        {
          property: `og:article:published_time`,
          content: finalMeta.opengraph.publishedTime,
        },
        {
          property: `article:modified_time`,
          content: finalMeta.opengraph.modifiedTime,
        },
        {
          property: `og:locale`,
          content: 'en',
        },
        {
          property: `og:image`,
          content: finalMeta.opengraph.image.url,
        },
        {
          property: `og:image:width`,
          content: finalMeta.opengraph.image.width,
        },
        {
          property: `og:image:height`,
          content: finalMeta.opengraph.image.height,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:title`,
          content: finalMeta.twitter.title,
        },
        {
          name: `twitter:description`,
          content: finalMeta.twitter.description,
        },
        {
          name: `twitter:site`,
          content: '@Maclynn_',
        },
        {
          name: `twitter:creator`,
          content: '@Maclynn_',
        },
        {
          name: `twitter:url`,
          content: '@Maclynn_',
        },
        {
          name: `twitter:image`,
          content: finalMeta.twitter.image.url,
        },
        {
          name: `google-site-verification`,
          content:
            SITE_CODE === 'US'
              ? 'XhjHv8fPH7M1uodlpQ2xjMoHwJbGIsFqOLl33PH3iDM'
              : 'W9B2NCTK7OPnzr6pSicyg4crQNRKQwTQqH6UqeujGHk',
        },
      ].concat(meta)}
    >
      <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
    </Helmet>
  );
}

Seo.defaultProps = {
  lang: `en`,
  meta: [],
  title: '',
  description: '',
  author: '',
  yoast: {},
  schemaMarkup: [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Maclynn',
      alternateName: 'Vida Consultancy Ltd',
    },
  ],
  socialImageUrl: null,
};

Seo.propTypes = {
  description: PropTypes.string,
  author: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string,
  yoast: PropTypes.shape({
    title: PropTypes.string,
    metaDesc: PropTypes.string,
    metaRobotsNoindex: PropTypes.string,
    metaRobotsNofollow: PropTypes.string,
    opengraphTitle: PropTypes.string,
    opengraphDescription: PropTypes.string,
    opengraphType: PropTypes.string,
    opengraphSiteName: PropTypes.string,
    opengraphPublishedTime: PropTypes.string,
    opengraphModifiedTime: PropTypes.string,
    opengraphImage: PropTypes.shape({
      publicUrl: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
    }),
    twitterTitle: PropTypes.string,
    twitterDescription: PropTypes.string,
    twitterImage: PropTypes.shape({
      publicUrl: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
    }),
  }),
  schemaMarkup: PropTypes.arrayOf(PropTypes.shape({})),
  socialImageUrl: PropTypes.string,
  locationData: PropTypes.shape({ pathname: PropTypes.string }).isRequired,
};
